<script lang="ts">
	import {
		addDays,
		addMonths,
		addWeeks,
		eachDayOfInterval,
		getDate,
		getMonth,
		getYear,
		isSameDay,
		isSameMonth,
		startOfMonth,
		startOfWeek,
		format,
		formatISO,
		endOfMonth,
	} from "date-fns";
	import { Card, LeftRight, HubProfileBottomLineButton } from "@hitit/new_ui";
	import { formatKrwNumber } from "@hitit/hub/src/v7/utils";
	import { t, api } from "../../../stores";
	import { Cach } from "../../class";

	export let storeId = "";
	export let date = new Date();
	export let today = new Date();

	const koreaTaxSchedule =
		"https://www.nts.go.kr/nts/ad/taxSchdul/selectList.do?mi=135747";
	const cache = new Cach();
	let total = 0;

	function loadCalendarMonth(date: Date) {
		const start = startOfWeek(startOfMonth(date));
		const end = addDays(addWeeks(start, 6), -1);
		const dateRange = eachDayOfInterval({ start, end }); // always 42 days
		const arr = [];
		for (let i = 0; i < dateRange.length; i += 7) {
			arr.push(dateRange.slice(i, i + 7));
		}

		return arr;
	}

	function actionHandler(add: number, __date: Date) {
		return () => {
			date = addMonths(__date, add);
		};
	}

	async function getTotal(__date: Date) {
		const start = startOfMonth(__date);
		const end = endOfMonth(__date);

		const response = await $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(start),
			endTimeInTimeZone: formatISO(end),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "YEARLY",
			storeid: storeId,
		});

		if (__date !== date) {
			return;
		}

		let amount = 0;
		for (const salesReport of response.data.salesReports) {
			amount += salesReport.sum;
		}

		total = amount;
	}

	async function loadDailyReport(date: Date[], storeId: string) {
		const firstDate = date[0];
		const lastDate = date[date.length - 1];

		const cacheStart = format(firstDate, "yyyy-MM-dd");
		const cacheEnd = format(lastDate, "yyyy-MM-dd");
		const cacheId = `${cacheStart}_${cacheEnd}`;
		const cacheData = cache.getCach(cacheId);

		if (cacheData) {
			return cacheData as { [index: string]: number };
		}
		const response = await $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(firstDate),
			endTimeInTimeZone: formatISO(lastDate),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "DAILY",
			storeid: storeId,
		});

		const reports: { [index: string]: number } = {};

		for (const report of response.data.salesReports) {
			const reportId = `${report.year}_${report.month}_${report.day}`;
			reports[reportId] = report.sum;
		}

		cache.setCach(cacheId, reports);
		return reports;
	}
	function parseDailyReportId(date: Date) {
		return `${getYear(date)}_${getMonth(date) + 1}_${getDate(date)}`;
	}

	function parseDailyAmount(amount?: number) {
		if (amount) {
			return `${formatKrwNumber(amount)}${$t("global.KRW")}`;
		}

		return "";
	}

	$: days = [
		$t("global.week_day.Sun"),
		$t("global.week_day.Mon"),
		$t("global.week_day.Tue"),
		$t("global.week_day.Wed"),
		$t("global.week_day.Thu"),
		$t("global.week_day.Fri"),
		$t("global.week_day.Sat"),
	];
	$: weeks = loadCalendarMonth(date);
	$: getTotal(date);
	$: dailyReport = loadDailyReport(weeks.flat(), storeId);

</script>

<Card noPadding>
	<div class="container">
		<div class="header">
			<div class="action">
				<button class="left-right" on:click={actionHandler(-1, date)}>
					<LeftRight fillMode />
				</button>
				<h1>{format(date, 'yyyy.MM')}</h1>
				<button class="left-right" on:click={actionHandler(1, date)}>
					<LeftRight fillMode direction={'right'} />
				</button>
			</div>
			<div class="total">
				<span class="label">Total</span>
				<span class="amount"> {formatKrwNumber(total)}{$t('global.KRW')} </span>
			</div>
		</div>
		<div class="table">
			<div class="t-head">
				{#each days as day}
					<div class="tr">
						<div class="th td">{day}</div>
					</div>
				{/each}
			</div>
			<div class="t-body">
				{#each weeks as week}
					<div class="tr">
						{#each week as day}
							<div
								class="td"
								class:isOutOfMonth={!isSameMonth(date, day)}
								class:isSameDay={isSameDay(today, day)}>
								<span class="date"> {getDate(day)} </span>
								<span class="amount">
									{#await dailyReport}
										...
									{:then report}
										{parseDailyAmount(report[parseDailyReportId(day)])}
									{/await}
								</span>
							</div>
						{/each}
					</div>
				{/each}
			</div>
		</div>
		<div class="tax-anchor">
			<HubProfileBottomLineButton href={koreaTaxSchedule} rightIconVisible -right-arrow>
				<span> {$t('hub.revenue.korea_tax_schedule')}</span>
			</HubProfileBottomLineButton>
		</div>
	</div>
</Card>

<style lang="scss">
	@import "../../../variables.scss";

	div {
		box-sizing: border-box;
	}

	button {
		border: none;
		background-color: transparent;
		outline: none;
		padding: 0;

		&:hover {
			cursor: pointer;
		}
	}

	.container {
		.tax-anchor {
			margin-left: 10px;
			width: 165px;

			span {
				color: #3c68d6;
			}
		}

		display: flex;
		flex-direction: column;
		padding: 20px 0px;
		width: 100%;
		gap: 20px;

		.header {
			display: flex;
			flex-direction: column;
			flex-grow: 1;
			justify-content: center;
			align-items: center;
			gap: 20px;

			.action {
				display: flex;
				flex-grow: 1;
				justify-content: center;
				align-items: center;
				gap: 20px;
				.left-right {
					width: 14px;
					height: 27.6px;
				}

				h1 {
					font-weight: 550;
					font-size: 1.875rem;
					color: #46474b;
				}
			}

			.total {
				display: flex;
				gap: 10px;
				align-items: center;

				.label {
					color: #bcbfc3;

					font-weight: 500;
				}

				.amount {
					font-weight: 600;
					font-size: 1.875rem;
					color: #46474b;
				}
			}
		}

		.table {
			width: 100%;
			.t-head {
				display: flex;
				width: 100%;
				background-color: #f0f0f4;

				.tr {
					height: 30px;
					justify-content: center;
					align-items: center;

					color: #46474b;
					font-weight: 500;

					&:first-child {
						color: #e54848;
					}
				}
			}

			.tr {
				display: flex;
				width: 100%;

				.td {
					max-width: calc(100% / 7);
					width: calc(100% / 7);
				}
			}

			.t-body {
				.tr {
					.td {
						&:first-child {
							.date {
								color: #e54848;
							}
						}

						&.isSameDay {
							color: #3c68d6;
							.date {
								color: #3c68d6;
								font-weight: 500;
							}
							.amount {
								font-weight: 550;
								color: #3c68d6;
							}
						}

						&.isOutOfMonth {
							opacity: 0.5;
						}

						display: flex;
						flex-direction: column;
						gap: 10px;
						color: #6d6f76;
						padding: 10px 0px;
						min-height: 76px;
						.date {
							font-weight: 200;
							font-size: 0.875rem;
						}
						.amount {
							font-size: 0.75rem;
							word-break: break-all;
							text-align: center;
							color: #46474b;
							font-weight: 500;
						}

						.date,
						.amount {
							display: flex;
							justify-content: center;
						}
					}
				}
			}
		}
	}

	@include DesktopSize {
		.container {
			:global(.-right-arrow) {
				height: 20px;
			}
			padding: 30px;
			.tax-anchor {
				width: 196px;
				margin-left: 0;
				font-size: 1.25rem;
			}
			.header {
				flex-direction: row;
				justify-content: space-between;

				.action {
					order: 2;
					flex-grow: 0;
					gap: 30px;
					.left-right {
						width: 10px;
						height: 19.8px;
					}
					h1 {
						font-size: 2.1875rem;
					}
				}

				.total {
					order: 1;
					align-items: baseline;

					.label {
						font-size: 1.25rem;
					}
					.amount {
						font-size: 2.1875rem;
					}
				}
			}

			.table {
				.t-head {
					border-radius: 22.5px;
					.tr {
						font-size: 1.5rem;
						height: 40px;
					}
				}
				.t-body {
					.tr {
						.td {
							gap: 30px;
							min-height: 130.5px;
							padding: 12px 0px;
							.date {
								font-size: 1rem;
							}
							.amount {
								font-size: 1.25rem;
								font-weight: 550;
							}
						}
					}
				}
			}
		}
	}

</style>
